import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { isCompareGraphOverlay } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { saveView } from 'components/Graph/commands/saveView';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { useLogError } from 'containers/ErrorBoundary/useLogError';
import { flow } from 'fp-ts/lib/function';
import type { FC } from 'react';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useGraphContextSelectors } from '../GraphContext';
import { SaveIcon } from '../GraphSidePanel/icons/SaveIcon';
import { GraphToolbarButton } from '../GraphToolbar/GraphToolbarButton';
import { SaveViewConfirmDialogBody } from './SaveViewConfirmDialogBody';

interface Props {
  disabled: boolean;
}

const CONFIRM_SAVE_VIEW_DIALOG = 'saveViewConfirmDialog';

export const SaveView: FC<Props> = (props) => {
  const [loadingState, setLoadingState] = useLoadingState();
  const logError = useLogError();
  const ctxProps = useGraphContextSelectors({
    compareActive: flow(prop('overlay'), isCompareGraphOverlay),
    dispatch: prop('dispatch'),
    loadedView: prop('loadViewDialog', 'loadedView'),
    sharedViewId: prop('sharedViewId'),
  });
  const msg = useLocale();
  const { showNotification } = useNotifications();
  const confirmDialog = useConfirmDialog({ name: CONFIRM_SAVE_VIEW_DIALOG });

  const disabled =
    ctxProps.loadedView === null ||
    props.disabled ||
    ctxProps.compareActive ||
    ctxProps.sharedViewId !== null;

  const openConfirmDialog = () => {
    if (!confirmDialog.dialogOpen) {
      confirmDialog.toggleConfirmDialog();
    }
  };

  const handleClickConfirm = async () => {
    if (loadingState !== 'LOADING') {
      try {
        await withLoadingIndicator(setLoadingState)(async () => {
          if (ctxProps.loadedView !== null) {
            await ctxProps.dispatch(saveView, { view: ctxProps.loadedView });
          }
        });
      } catch (e) {
        logError(e, 'GRAPH');
        showNotification({
          type: 'danger',
          message: msg.canvasGraph.commands.saveView.cannotSaveView,
        });
      }
    }
  };

  return (
    <>
      <ConfirmDialog
        bodyComponent={
          ctxProps.loadedView ? (
            <SaveViewConfirmDialogBody view={ctxProps.loadedView} />
          ) : undefined
        }
        id={CONFIRM_SAVE_VIEW_DIALOG}
        msg={msg.canvasGraph.commands.saveView.confirmDialog}
        btnCancelAccent='positive'
        btnCancelVariant='secondary'
        btnConfirmAccent='negative'
        btnConfirmVariant='primary'
        onClickConfirm={handleClickConfirm}
      />
      <GraphToolbarButton
        disabled={disabled}
        disabledTitle={
          props.disabled
            ? msg.canvasGraph.graphToolbar.buttons.selectDataToDisplay
            : msg.canvasGraph.graphToolbar.buttons.saveViewDisabledNoView
        }
        onClick={openConfirmDialog}
        title={msg.canvasGraph.graphToolbar.buttons.saveView}
      >
        {loadingState === 'LOADING' ? (
          <div className='graph-save-view__spinner-wrapper'>
            <i className='fa-regular fa-spinner fa-spin fa-fw' />
          </div>
        ) : (
          <SaveIcon title='Save' height={16} width={16} />
        )}
      </GraphToolbarButton>
    </>
  );
};
